/**
 * Proof page — live contract links, launch status, and user-facing safeguards.
 * Read-only. No wallet interaction.
 */
import { useMemo, useState, useEffect } from "react";
import { ADDRESS_ENV, EXPECTED_CHAIN_LABEL, envAddress } from "../utils/config";

const DISCLOSURES = [
  {
    id: "Marketplace fees",
    title: "NFT Fees Follow the Current Owner",
    badge: "badge-gold",
    summary: "Unclaimed WETH belongs to whoever owns the NFT at claim time.",
    points: [
      "If you hold an active CODE APE, pending WETH can be claimed by the current NFT owner.",
      "If you sell or transfer the NFT, future fee rights move with the NFT.",
      "If you burn the NFT, pending WETH is settled first and future fee rights stop.",
      "Before listing or buying on a marketplace, check the token page or contract reads for pending fees.",
      "Off-site marketplaces may not display pending WETH in their listing price."
    ]
  },
  {
    id: "Wallet-gated generation",
    title: "Wallet-Gated Summon",
    badge: "badge-prime",
    summary: "Your wallet controls the summon. No ETH moves during auth.",
    points: [
      "Start at AI Agent Auth, sign a login message, then let the Skill create your mint handoff.",
      "The handoff is bound to your wallet, the NFT contract, the chain, and the generated hashes.",
      "The mint page only asks your wallet to submit the final mint transaction.",
      "Small balance and quota checks protect the generation service from spam.",
      "The signature is for access only; it is not a mint transaction."
    ]
  },
  {
    id: "Router compatibility",
    title: "Official Pool Swap Rules",
    badge: "badge-cyan",
    summary: "The official pool routes a 1% WETH fee stream to active NFTs.",
    points: [
      "The hook is designed for exact-input swaps in the official Uniswap v4 pool.",
      "Sell-side swaps can return less WETH than a no-hook quote because the holder fee is collected.",
      "Routers should use minOut and slippage checks that include the hook fee.",
      "Third-party routers may need integration updates before every route works smoothly."
    ]
  },
  {
    id: "No hidden buyback",
    title: "No Hidden Treasury Switch",
    badge: "badge-green",
    summary: "The launch contracts keep the holder-fee design simple.",
    points: [
      "Hook fees are WETH-denominated for active NFTs.",
      "There is no hidden CAPE fee accumulator.",
      "There is no automatic buyback promise.",
      "There is no admin hook-fee withdrawal path in the launch contracts."
    ]
  },
  {
    id: "UNCX LP lock",
    title: "365-Day LP Lock Path",
    badge: "badge-prime",
    summary: "When launch finalizes, initial LP is designed to lock before trading opens.",
    points: [
      "LiquidityVault365 uses a 31,536,000 second lock window, about 365 days.",
      "Finalize must mint the Uniswap v4 LP NFT and lock it through UNCX before trading opens.",
      "After finalize, this page will show the LP token id, UNCX lock id, lock owner, and unlock date.",
      "The published lock owner can extend the lock, or recover LP after unlock if the project is discontinued.",
      "NFT WETH fee distribution is separate from the LP lock and remains handled by CodeApeHook."
    ]
  },
  {
    id: "Admin status",
    title: "Launch Settings Are Locked",
    badge: "badge-cyan",
    summary: "Mint is open, the signer is frozen, metadata is frozen, and NFT launch admin is renounced.",
    points: [
      "The live NFT reports launchAdmin as the zero address.",
      "The official signer is frozen, so the voucher signer cannot be swapped by launch admin.",
      "Metadata configuration is frozen for the launch.",
      "openMint is one-shot and already active.",
      "opsRecipient and lpLockOwner are published in the deployment manifest and cannot be changed inside LiquidityVault365."
    ]
  }
];


const FOUR_FIRSTS = [
  {
    index: "01",
    label: "Hook to Holders",
    body: "First v4 hook routing pool fees to NFT holders, not LP.",
    evidence: "CodeApeHook.afterSwap -> accWethFeePerActiveNFT (1e36 precision)"
  },
  {
    index: "02",
    label: "3-Hash Provenance",
    body: "Skill version, image, and handoff hashes per token.",
    evidence: "CodeApeNFT.skillVersionHash / imageHash / handoffHash (per tokenId)"
  },
  {
    index: "03",
    label: "AI Handoff Gate",
    body: "Mint requires a signed EIP-712 voucher tied to a public Skill handoff.",
    evidence: "CodeApeNFT.mintWithVoucher rejects any tx without a valid voucher"
  },
  {
    index: "04",
    label: "Stakeless Fee Stream",
    body: "NFT itself is the bearer of fee accrual. No staking contract exists.",
    evidence: "No stake() / lock() / unlock() / withdrawalQueue() in the codebase"
  }
] as const;

const BOUNDARY_ITEMS = [
  { label: "Voucher-only mint",            icon: "✅", detail: "Every mint needs an official signed voucher bound to the wallet and generated hashes." },
  { label: "Mint is final",                icon: "✅", detail: "There is no reversal or mint-cancel function after a wallet mints." },
  { label: "No pause switch",              icon: "✅", detail: "The launch contracts are not upgradeable and do not include a pause button." },
  { label: "No admin mint",                icon: "✅", detail: "There is no free admin mint path. Mint supply comes through user vouchers." },
  { label: "No hidden CAPE fee",           icon: "✅", detail: "Hook fees are WETH-denominated for active NFTs, not a hidden CAPE tax." },
  { label: "No buyback promise",           icon: "✅", detail: "The launch does not depend on an automatic buyback story." },
  { label: "Public LP lock path",          icon: "✅", detail: "Finalize is designed to publish UNCX lock evidence before trading opens." },
];

export default function ProofPage() {
  const [manifest, setManifest] = useState<any>(null);
  const [evidence, setEvidence] = useState<any>(null);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const manifestIsStaging = Boolean(manifest?.status?.includes("STAGING") || manifest?.network?.chainId === "11155111");
  const liquidityLock = manifest?.liquidityLock;
  const liquidityLockEvidence = liquidityLock?.evidence;
  const liquidityLockReady = Boolean(liquidityLockEvidence?.lpTokenId && liquidityLockEvidence?.uncxLockId && liquidityLockEvidence?.unlockTime);
  const sourceVerification = manifest?.sourceVerification;
  const sourceVerificationRows = useMemo(() => buildSourceVerificationRows(manifest), [manifest]);
  const adminStatusRows = useMemo(() => buildAdminStatusRows(manifest), [manifest]);
  const addressRows = useMemo(() => buildAddressRows(manifest), [manifest]);

  async function copyAddress(label: string, address: string) {
    await navigator.clipboard?.writeText(address);
    setCopiedAddress(label);
    window.setTimeout(() => setCopiedAddress((current) => current === label ? null : current), 1400);
  }

  useEffect(() => {
    // Attempt to load from public folder where they would be deployed
    fetch("/proof-manifest.json").then(r => r.json()).then(setManifest).catch(() => {});
    fetch("/router-compatibility-evidence.json").then(r => r.json()).then(setEvidence).catch(() => {});
  }, []);
  return (
    <main className="page">
      <div className="container" style={{ maxWidth: 880, paddingBottom: "var(--space-24)" }}>
        <div style={{ marginBottom: "var(--space-8)" }}>
          <h1 className="text-4xl" style={{ marginBottom: "var(--space-3)" }}>
            Protocol <span className="gradient-text">Proof</span>
          </h1>
          <p className="text-muted">
            The live contract set, source links, launch status, and wallet rules in one place.
            No wallet connection is needed on this page.
          </p>
          {manifest && (
            <div className={manifestIsStaging ? "alert alert-warning" : "alert alert-info"} style={{ marginTop: "var(--space-4)" }}>
              <div>
                <strong>{manifestIsStaging ? "Sepolia staging draft:" : "Live manifest:"}</strong>
                <div className="text-sm" style={{ marginTop: 4, overflowWrap: "anywhere" }}>
                  {formatNetworkName(manifest.network?.name)} · {formatManifestStatus(manifest.status)}
                  {manifestIsStaging ? " · Not mainnet proof." : ""}
                </div>
              </div>
            </div>
          )}
          {evidence && (
            <div className="alert alert-success" style={{ marginTop: "var(--space-4)" }}>
              <div>
                <strong>Official Pool Rule Check</strong>
                <div className="text-sm" style={{ marginTop: 4, overflowWrap: "anywhere" }}>
                  Sell fee mode {evidence.sellSideFeeMode} is published for route builders.
                </div>
              </div>
            </div>
          )}
          {liquidityLock && (
            <div className="alert alert-info" style={{ marginTop: "var(--space-4)" }}>
              <div>
                <strong>365-Day LP Lock Path</strong>
                <div className="text-sm" style={{ marginTop: 4, overflowWrap: "anywhere" }}>
                  LP auto-lock into UNCX · {formatSeconds(liquidityLock.lockDurationSeconds)} · {liquidityLockReady
                    ? `LP token ${liquidityLockEvidence.lpTokenId} · lock ${liquidityLockEvidence.uncxLockId} · unlock ${liquidityLockEvidence.unlockTime}`
                    : "lock evidence pending finalize after sell-out or deadline"}
                </div>
              </div>
            </div>
          )}
          {sourceVerification && (
            <div className="alert alert-success" style={{ marginTop: "var(--space-4)" }}>
              <div>
                <strong>Verified Source</strong>
                <div className="text-sm" style={{ marginTop: 4, overflowWrap: "anywhere" }}>
                  {formatSourceVerificationStatus(sourceVerification.status)} · {sourceVerification.explorer || "Explorer"}
                </div>
              </div>
            </div>
          )}
        </div>


        <section style={{ marginBottom: "var(--space-12)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "var(--space-4)", flexWrap: "wrap", marginBottom: "var(--space-4)" }}>
            <div>
              <h2 className="text-2xl" style={{ marginBottom: "var(--space-2)" }}>Contract Addresses</h2>
              <p className="text-sm text-muted">Copy the live addresses before minting, claiming fees, or checking your Ape.</p>
            </div>
            <span className="badge badge-cyan">{EXPECTED_CHAIN_LABEL}</span>
          </div>
          <div className="card" style={{ padding: "var(--space-4)", display: "grid", gap: "var(--space-3)" }}>
            {addressRows.map((row) => (
              <div key={row.label} className="notranslate" translate="no" style={{ display: "grid", gridTemplateColumns: "minmax(120px, 0.45fr) minmax(0, 1fr) auto", gap: "var(--space-3)", alignItems: "center" }}>
                <div className="notranslate" translate="no" style={{ fontWeight: 700, color: row.required ? "var(--text-primary)" : "var(--text-muted)", overflowWrap: "anywhere" }}>{row.label}</div>
                <code className="notranslate" translate="no" style={{ fontFamily: "var(--text-mono)", fontSize: "var(--font-sm)", color: row.address ? "var(--color-prime)" : "var(--text-muted)", overflowWrap: "anywhere", wordBreak: "break-all" }}>
                  {row.address || row.emptyText}
                </code>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => row.address && copyAddress(row.label, row.address)}
                  disabled={!row.address}
                  style={{ padding: "0.55rem 0.85rem", minHeight: 0 }}
                >
                  <span className="notranslate" translate="no">{copiedAddress === row.label ? "Copied" : "Copy"}</span>
                </button>
              </div>
            ))}
          </div>
        </section>

        {sourceVerificationRows.length > 0 && (
          <section style={{ marginBottom: "var(--space-12)" }}>
            <h2 className="text-2xl" style={{ marginBottom: "var(--space-3)" }}>Open Source Contracts</h2>
            <p className="text-sm text-muted" style={{ marginBottom: "var(--space-4)" }}>
              Every core contract is verified on Etherscan, so anyone can inspect the exact code behind each live address before using it.
            </p>
            <div className="card" style={{ padding: "var(--space-4)", display: "grid", gap: "var(--space-3)" }}>
              {sourceVerificationRows.map((row) => (
                <div key={row.label} className="notranslate" translate="no" style={{ display: "grid", gridTemplateColumns: "minmax(120px, 0.35fr) minmax(0, 1fr) auto", gap: "var(--space-3)", alignItems: "center" }}>
                  <div style={{ fontWeight: 700 }}>{row.label}</div>
                  <code style={{ fontFamily: "var(--text-mono)", fontSize: "var(--font-sm)", color: "var(--color-prime)", overflowWrap: "anywhere", wordBreak: "break-all" }}>{row.address}</code>
                  <a className="btn btn-secondary" href={row.url} target="_blank" rel="noreferrer" style={{ padding: "0.55rem 0.85rem", minHeight: 0 }}>
                    View Code
                  </a>
                </div>
              ))}
            </div>
          </section>
        )}

        {adminStatusRows.length > 0 && (
          <section style={{ marginBottom: "var(--space-12)" }}>
            <h2 className="text-2xl" style={{ marginBottom: "var(--space-3)" }}>Launch Safety Status</h2>
            <p className="text-sm text-muted" style={{ marginBottom: "var(--space-4)" }}>
              Key launch settings are visible on chain. The NFT launch admin is renounced, and the signer and metadata settings are frozen.
            </p>
            <div className="card" style={{ padding: "var(--space-4)", display: "grid", gap: "var(--space-3)" }}>
              {adminStatusRows.map((row) => (
                <div key={row.label} className="kv-row">
                  <span>{row.label}</span>
                  <strong className="notranslate" translate="no" style={{ color: row.good ? "var(--color-prime)" : "var(--color-gold)", overflowWrap: "anywhere" }}>{row.value}</strong>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="proof-firsts" aria-label="CODE APE feature proof" style={{ marginBottom: "var(--space-12)" }}>
          <h2 className="text-2xl" style={{ marginBottom: "var(--space-3)" }}>Why CODE APE Is Different</h2>
          <p className="text-muted" style={{ marginBottom: "var(--space-5)" }}>
            CODE APE is built around four visible mechanics: AI-agent handoff minting, on-chain provenance, NFT-held WETH fees, and burn-to-claim CAPE.
          </p>
          <div className="proof-firsts__grid">
            {FOUR_FIRSTS.map((item) => (
              <article key={item.index} className="proof-firsts__item">
                <header><span>{item.index}</span><strong>{item.label}</strong></header>
                <p>{item.body}</p>
                <code>{item.evidence}</code>
              </article>
            ))}
          </div>
        </section>

        {/* Launch safety checklist */}
        <section style={{ marginBottom: "var(--space-12)" }}>
          <h2 className="text-2xl" style={{ marginBottom: "var(--space-6)" }}>
            🔒 Launch Safety Checklist
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            {BOUNDARY_ITEMS.map((item) => (
              <div key={item.label} className="card" style={{ padding: "var(--space-4) var(--space-6)", display: "flex", alignItems: "flex-start", gap: "var(--space-4)" }} id={`check-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <span style={{ fontSize: "1.25rem", flexShrink: 0 }}>{item.icon}</span>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>{item.label}</div>
                  <div className="text-sm text-muted">{item.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* User notes */}
        <section>
          <h2 className="text-2xl" style={{ marginBottom: "var(--space-6)" }}>
            📋 What to Know Before You Mint
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
            {DISCLOSURES.map((d) => (
              <div key={d.id} className="card" id={`note-${d.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-4)" }}>
                  <span className={`badge ${d.badge}`}>Note</span>
                  <h3 className="text-xl">{d.title}</h3>
                </div>
                <p className="text-muted" style={{ marginBottom: "var(--space-4)", fontStyle: "italic" }}>{d.summary}</p>
                <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                  {d.points.map((p) => (
                    <li key={p} style={{ display: "flex", gap: "var(--space-3)", fontSize: "var(--font-sm)", color: "var(--text-secondary)" }}>
                      <span style={{ color: "var(--color-prime)", flexShrink: 0 }}>→</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}


function buildAddressRows(manifest: any): Array<{ label: string; address: string | null; required: boolean; emptyText: string }> {
  const contracts = manifest?.contracts ?? {};
  const infra = manifest?.officialInfrastructure ?? {};
  return [
    { label: "CodeApeNFT", address: contracts.codeApeNFT ?? envAddress(ADDRESS_ENV.nft), required: true, emptyText: "Not configured" },
    { label: "CodeApeHook", address: contracts.codeApeHook ?? envAddress(ADDRESS_ENV.hook), required: true, emptyText: "Not configured" },
    { label: "ClaimVault", address: contracts.claimVault ?? envAddress(ADDRESS_ENV.claimVault), required: true, emptyText: "Not configured" },
    { label: "LiquidityVault", address: contracts.liquidityVault ?? null, required: true, emptyText: "Not configured" },
    { label: "CapeToken", address: contracts.capeToken ?? null, required: true, emptyText: "Not configured" },
    { label: "ProjectRouter", address: contracts.projectRouter ?? null, required: false, emptyText: "Not used in current launch" },
    { label: "PoolManager", address: infra.poolManager ?? null, required: false, emptyText: "Not configured" },
    { label: "PositionManager", address: infra.positionManager ?? null, required: false, emptyText: "Not configured" },
    { label: "UNCXLocker", address: infra.uncxLocker ?? null, required: false, emptyText: "Not configured" },
    { label: "WETH", address: infra.weth ?? null, required: false, emptyText: "Not configured" }
  ];
}

function buildSourceVerificationRows(manifest: any): Array<{ label: string; address: string; url: string }> {
  const contracts = manifest?.sourceVerification?.contracts ?? {};
  return Object.entries(contracts)
    .map(([label, value]) => {
      const item = value as { address?: string; url?: string };
      return item.address && item.url ? { label, address: item.address, url: item.url } : null;
    })
    .filter((item): item is { label: string; address: string; url: string } => Boolean(item));
}

function buildAdminStatusRows(manifest: any): Array<{ label: string; value: string; good: boolean }> {
  const ops = manifest?.ops ?? {};
  const lock = manifest?.liquidityLock ?? {};
  return [
    { label: "NFT launchAdmin", value: "Renounced to zero address", good: true },
    { label: "Official signer", value: "Locked after deployment", good: true },
    { label: "Metadata config", value: "Locked after deployment", good: true },
    { label: "openMint", value: "One-shot launch action already completed", good: true },
    { label: "opsRecipient", value: ops.opsRecipient ?? "Published in manifest", good: false },
    { label: "lpLockOwner", value: ops.lpLockOwner ?? lock.lpLockOwner ?? "Published in manifest", good: false },
    { label: "LP lock duration", value: formatSeconds(lock.lockDurationSeconds), good: true }
  ];
}

function formatSeconds(value: unknown): string {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return "duration pending";
  const days = Math.round(seconds / 86400);
  return `${seconds.toLocaleString()} seconds (${days.toLocaleString()} days)`;
}

function formatNetworkName(value: unknown): string {
  if (value === "ethereum-mainnet") return "Ethereum mainnet";
  if (value === "sepolia") return "Sepolia";
  return typeof value === "string" && value.trim() ? value : "Ethereum";
}

function formatManifestStatus(value: unknown): string {
  if (value === "MAINNET_DEPLOYED_MINT_OPEN_NOT_FINALIZED") return "Mint open; launch finalization pending";
  if (value === "MAINNET_FINALIZED_TRADING_PENDING") return "Finalized; trading opens after the on-chain delay";
  if (value === "MAINNET_FINALIZED_TRADING_OPEN") return "Finalized; trading open";
  if (typeof value === "string" && value.includes("STAGING")) return "Staging";
  return "Live launch status published";
}

function formatSourceVerificationStatus(value: unknown): string {
  if (value === "PASS") return "All core contracts verified";
  if (value === "PARTIAL") return "Verification partially complete";
  return "Source links published";
}
